/** An on-site sub-location is named `aisle.rack.shelf`. */
const SUBLOCATION_PATTERN = /^[^.]+\.[^.]+\.[^.]+$/;

export interface SublocationSearchResult {
  location: string;
  valid: boolean;
  new: boolean;
  /** Free-text mode only: the value is not a known sub-location (warn, never create). */
  unknown?: boolean;
  /** Bug 36439: a well-formed name with no such bin (not pickable; the row says it does not exist). */
  notFound?: boolean;
}

/** The app-selected company and warehouse a sub-location list is loaded for. */
export interface SublocationScope {
  companyId: number;
  warehouseId: number;
  warehouseName: string;
}

export interface SublocationSearchOptions {
  /** Keep names that are not `a.b.c`. */
  offSite?: boolean;
  /**
   * WI 34968: accept any text. An unknown value is pickable and flagged `unknown`, never `new`,
   * so nothing downstream creates a sub-location from it.
   */
  freeText?: boolean;
}

/**
 * Searches the cached sub-location names. Without `freeText` this is the Tool Tracker rule the
 * stock, transfer and print pages rely on: on-site lists only `a.b.c` names, off-site lists any
 * name, and an unknown query is offered as invalid, never as new (Bug 36439).
 */
export function searchSublocationNames(
  names: Array<string>,
  query: string,
  opts: SublocationSearchOptions
): Array<SublocationSearchResult> {
  const search = (query ?? '').toLowerCase();
  const matches = names
    .filter((name) => name !== '')
    .filter(
      (name) => opts.freeText || opts.offSite || SUBLOCATION_PATTERN.test(name)
    )
    .filter((name) => name.toLowerCase().indexOf(search) > -1);

  const results = [...new Set(matches)].map((location) => ({
    location,
    valid: true,
    new: false,
  }));
  if (opts.freeText) {
    return freeTextResults(results, query);
  }
  if (results.length) {
    return results;
  }

  // Bug 36439 (Halo 99499): the phone never creates a bin; new bins are made on web. A
  // well-formed name (any off-site text, an on-site a.b.c) is a bin that does not exist.
  if (query && (opts.offSite || SUBLOCATION_PATTERN.test(query))) {
    return [{ location: query, valid: false, new: false, notFound: true }];
  }
  return [{ location: query, valid: false, new: false }];
}

/**
 * Free text: the first row is what Return picks, so it is an exact match when one exists, else the
 * typed text itself, never a name that merely contains it (`Bench` must not save `Bench 2`).
 */
function freeTextResults(
  matches: Array<SublocationSearchResult>,
  query: string
): Array<SublocationSearchResult> {
  if (!query) {
    return matches;
  }
  const target = query.toLowerCase();
  const exact = matches.filter((r) => r.location.toLowerCase() === target);
  const others = matches.filter((r) => r.location.toLowerCase() !== target);
  const first = exact.length
    ? exact
    : [{ location: query, valid: true, new: false, unknown: true }];
  return [...first, ...others];
}

/** True when the value is blank or names a cached sub-location (case-insensitive). */
export function isKnownSublocation(
  names: Array<string>,
  value: string
): boolean {
  if (!value) {
    return true;
  }
  const target = value.toLowerCase();
  return names.some((name) => name.toLowerCase() === target);
}

/** The text a sub-location control shows: a saved string, a picked record, or a tap wrapper. */
export function sublocationLabel(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value !== 'object') {
    return `${value}`;
  }
  const record = (value as { item?: unknown }).item ?? value;
  return (record as { location?: string }).location ?? '';
}

/** Names the warehouse the list is scoped to, so a mismatch with the form is never silent. */
export function sublocationScopeHint(scope: SublocationScope | null): string {
  return scope
    ? `Sub-locations for ${scope.warehouseName}`
    : 'No warehouse selected in Settings, so no sub-locations are listed';
}

/**
 * The scope from the app's saved company and warehouse. The list endpoint takes the Viewpoint
 * company NUMBER and the warehouse LOCATION code, as the Tool Tracker pages pass them.
 */
export function sublocationScopeFrom(
  company: { CompanyNumber?: number } | null,
  warehouse: { location?: string; warehouseName?: string } | null
): SublocationScope | null {
  if (company?.CompanyNumber == null || !warehouse?.location) {
    return null;
  }
  return {
    companyId: company.CompanyNumber,
    warehouseId: +warehouse.location,
    warehouseName: warehouse.warehouseName ?? warehouse.location,
  };
}

/** Replaces the scope hint when the list could not be loaded, so it never claims a list. */
export function sublocationLoadFailedHint(scope: SublocationScope): string {
  return `Could not load sub-locations for ${scope.warehouseName}. You can still type a location.`;
}

/**
 * WI 34981: the names a web autocomplete offers under free text. The typed text is already the
 * value there, so only known names are offered: an exact name first (it may fix the casing),
 * then names containing the text.
 */
export function sublocationSuggestions(
  names: Array<string>,
  query: string,
  limit = 50
): Array<string> {
  const target = (query ?? '').toLowerCase();
  const matches = [...new Set(names.filter((name) => !!name))].filter(
    (name) => name.toLowerCase().indexOf(target) > -1
  );
  const exact = matches.filter((name) => name.toLowerCase() === target);
  const others = matches.filter((name) => name.toLowerCase() !== target);
  return [...exact, ...others].slice(0, limit);
}
